import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useParams } from "react-router-dom";
import {
    getEventImagePresignedUrls,
    uploadImageToS3,
    createEvent,
    updateEvent,
    getEventById,
    getUsers,
    assignPhotographers,
} from "../../Services/arndAPI";
import Loader from "../../Components/Loader/Loader";
import styles from "./Styles/Event.module.css";

export default function SingleEvent() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isCreate = id === "create";

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("");
    const [photographers, setPhotographers] = useState([]);
    const [selectedPhotographer, setSelectedPhotographer] = useState("");

    const [form, setForm] = useState({
        name: "",
        date: "",
        location: "",
        eventType: "",
        iconFile: null,
        bannerFile: null,
        iconUrl: "",
        eventBanner: "",
    });

    // Fetch event + photographers when editing
    useEffect(() => {
        if (!isCreate) {
            fetchEvent();
            fetchPhotographers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchEvent = async () => {
        setLoading(true);
        try {
            const { eventData } = await getEventById(id);
            setForm({
                name: eventData.name,
                date: eventData.date ? eventData.date.substring(0, 10) : "",
                location: eventData.location || "",
                eventType: eventData.eventType || "",
                iconFile: null,
                bannerFile: null,
                iconUrl: eventData.iconUrl || "",
                eventBanner: eventData.eventBanner || "",
            });

            // pre-select the first assigned photographer if exists
            if (eventData.User && eventData.User.length > 0) {
                setSelectedPhotographer(eventData.User[0].id);
            }
        } catch (err) {
            console.error(err);
            setStatus("Error loading event");
        } finally {
            setLoading(false);
        }
    };

    const fetchPhotographers = async () => {
        try {
            const data = await getUsers({ role: "PHOTOGRAPHER" });
            setPhotographers(data);
        } catch (err) {
            setStatus("Error loading photographers");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus(isCreate ? "Creating event..." : "Updating event...");
        setLoading(true);

        try {
            let { iconUrl, eventBanner } = form;

            if (isCreate) {
                // Create mode
                const iconKey = `icons/${uuidv4()}`;
                const bannerKey = `banners/${uuidv4()}`;

                const { iconUrl: presignedIconUrl, bannerUrl: presignedBannerUrl } =
                    await getEventImagePresignedUrls(iconKey, bannerKey);

                if (form.iconFile) {
                    await uploadImageToS3(presignedIconUrl, form.iconFile);
                    iconUrl = presignedIconUrl.split("?")[0];
                }
                if (form.bannerFile) {
                    await uploadImageToS3(presignedBannerUrl, form.bannerFile);
                    eventBanner = presignedBannerUrl.split("?")[0];
                }

                const payload = {
                    name: form.name,
                    date: new Date(form.date).toISOString(),
                    location: form.location,
                    eventType: form.eventType,
                    iconUrl,
                    eventBanner,
                };

                await createEvent(payload);
                setStatus("Event created!");
            } else {
                // Edit mode
                const iconKey = form.iconUrl.split("/").slice(-2).join("/");
                const bannerKey = form.eventBanner.split("/").slice(-2).join("/");

                const { iconUrl: presignedIconUrl, bannerUrl: presignedBannerUrl } =
                    await getEventImagePresignedUrls(iconKey, bannerKey);

                if (form.iconFile) {
                    await uploadImageToS3(presignedIconUrl, form.iconFile);
                    iconUrl = presignedIconUrl.split("?")[0];
                }
                if (form.bannerFile) {
                    await uploadImageToS3(presignedBannerUrl, form.bannerFile);
                    eventBanner = presignedBannerUrl.split("?")[0];
                }

                const payload = {
                    name: form.name,
                    date: new Date(form.date).toISOString(),
                    location: form.location,
                    eventType: form.eventType,
                    iconUrl,
                    eventBanner,
                };

                await updateEvent(id, payload);

                // update photographer assignment if selected
                if (selectedPhotographer) {
                    await assignPhotographers(id, [selectedPhotographer]);
                }

                setStatus("Event updated!");
            }
            setLoading(false);
            navigate("/events");
        } catch (err) {
            console.error(err);
            setStatus("Error saving event");
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <h2>{isCreate ? "Create Event" : "Edit Event"}</h2>
            {loading ? (
                <Loader />
            ) : (
                <form className={styles.formContainer} onSubmit={handleSubmit}>
                    <input
                        type="text"
                        placeholder="Event Name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                    />
                    <input
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                        required
                    />
                    <input
                        type="text"
                        placeholder="Location"
                        value={form.location}
                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                        required
                    />
                    <input
                        type="text"
                        placeholder="Event Type"
                        value={form.eventType}
                        onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                        required
                    />

                    {/* Icon upload */}
                    <div className={styles.fileInput}>
                        <label>Icon Image:</label>
                        <div className={styles.imagePreview}>
                            {form.iconFile ? (
                                <img src={URL.createObjectURL(form.iconFile)} alt="icon preview" />
                            ) : form.iconUrl ? (
                                <img src={form.iconUrl} alt="current icon" />
                            ) : (
                                <div className={styles.placeholder}>No icon selected</div>
                            )}
                        </div>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setForm({ ...form, iconFile: e.target.files[0] })}
                            required={isCreate}
                        />
                    </div>

                    {/* Banner upload */}
                    <div className={styles.fileInput}>
                        <label>Banner Image:</label>
                        <div className={styles.imagePreview}>
                            {form.bannerFile ? (
                                <img src={URL.createObjectURL(form.bannerFile)} alt="banner preview" />
                            ) : form.eventBanner ? (
                                <img src={form.eventBanner} alt="current banner" />
                            ) : (
                                <div className={styles.placeholder}>No banner selected</div>
                            )}
                        </div>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setForm({ ...form, bannerFile: e.target.files[0] })}
                            required={isCreate}
                        />
                    </div>

                    {/* Photographer dropdown (only edit mode) */}
                    {!isCreate && (
                        <div className={styles.fileInput}>
                            <label>Assign Photographer:</label>
                            <select
                                value={selectedPhotographer}
                                onChange={(e) => setSelectedPhotographer(e.target.value)}
                            >
                                <option value="">-- Select Photographer --</option>
                                {photographers.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.fullName || p.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <button type="submit" className={styles.saveBtn}>
                        {isCreate ? "Create Event" : "Update Event"}
                    </button>
                    {status && <div className={styles.status}>{status}</div>}
                </form>
            )}
        </div>
    );
}
